import { useStore } from '@xyflow/react';
import { Pencil } from 'lucide-react';
import type { RunDayDivider } from '@mct/shared';
import { cn } from '../../lib/utils';

/**
 * Top-pinned label layer for the DAG day-divider lines. Subscribes to
 * the ReactFlow viewport transform so each chip stays directly above
 * its line as the user pans / zooms. Y is fixed at the top of the
 * canvas (8 px) so the labels are always visible even while the user
 * scrolls vertically through the graph.
 *
 * Click a chip to open the editor (rename / date / delete). The chip
 * itself doesn't claim any pointer events outside of its hit area so
 * the underlying canvas pan / select still works elsewhere.
 */
export function DayDividerLabels({
  dividers,
  readOnly,
  onEdit,
}: {
  dividers: readonly RunDayDivider[];
  readOnly: boolean;
  onEdit: (id: string, anchor: DOMRect) => void;
}) {
  // Subscribe to the transform's individual scalars so each useStore
  // selector returns a primitive — Object.is equality stays stable
  // across store updates that don't actually change the value. Pulling
  // the [tx, ty, zoom] tuple in one go would make the selector return
  // a fresh array reference every emit and re-render this layer on
  // every node update, contributing to render-loop pressure.
  const tx = useStore((s) => s.transform[0]);
  const zoom = useStore((s) => s.transform[2]);

  // Pushed below the floating toolbar (top-3 + p-2 + button row ≈
  // 56 px) PLUS a 24 px breathing-room gap so the chip doesn't visually
  // touch the bottom of the toolbar bar. z-30 keeps the chips above
  // the toolbar's bg-white/90 backdrop in case the user scrolls a
  // chip behind one of the controls horizontally.
  const LABEL_TOP_PX = 84;
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-30 h-28">
      {dividers.map((d) => {
        const screenX = tx + d.x * zoom;
        return (
          <button
            key={d.id}
            type="button"
            onClick={(e) => {
              if (readOnly) return;
              e.stopPropagation();
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              onEdit(d.id, rect);
            }}
            className={cn(
              'pointer-events-auto absolute -translate-x-1/2 rounded-md border px-2.5 py-1 text-xs font-semibold shadow-md',
              readOnly
                ? 'cursor-default border-indigo-300 bg-indigo-50 text-indigo-800'
                : 'cursor-pointer border-indigo-400 bg-indigo-500 text-white hover:bg-indigo-600',
            )}
            style={{ left: screenX, top: LABEL_TOP_PX }}
            aria-label={`日区切り: ${d.label || '(無題)'}`}
          >
            <span className="inline-flex items-center gap-1">
              {d.label || '(無題)'}
              {!readOnly ? <Pencil className="h-3 w-3 opacity-80" /> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}
